import {
    IonAvatar,
    IonChip,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonSpinner
} from '@ionic/react';
import { addCircle, closeCircle, personCircleOutline } from 'ionicons/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useProfileSearch } from '../../hooks/useProfileSearch';
import { SportType } from '../../models/sportType';
import { StatsUserInfo } from '../../models/userStats';
import StatsService from '../../services/stats.service';
import EclipticSearchbar from '../ComponentsUI/EclipticSearchbar/EclipticSearchbar';

interface UserComparisonSelectorProps {
    currentUserId: string;
    selectedUsers: StatsUserInfo[];
    onUsersChange: (users: StatsUserInfo[]) => void;
    maxUsers?: number;
    sport?: SportType;
}

const UserComparisonSelector: React.FC<UserComparisonSelectorProps> = ({
    currentUserId,
    selectedUsers,
    onUsersChange,
    maxUsers = 4,
    sport
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [suggestedUsers, setSuggestedUsers] = useState<StatsUserInfo[]>([]);

    // Use the profile search hook
    const {
        data: searchData,
        isLoading: loading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = useProfileSearch(debouncedQuery);

    // Map search results to StatsUserInfo format and filter out current user and already selected users
    const searchResults = useMemo(() => {
        if (!searchData?.pages) return [];

        const allProfiles = searchData.pages.flatMap(page => page.profiles);
        return allProfiles
            .filter(u => u.id !== currentUserId && !selectedUsers.some(s => s.id === u.id))
            .map(profile => ({
                id: profile.id,
                username: profile.username,
                name: profile.name,
                profile_pic: profile.profile_pic,
                home_mountain: profile.home_mountain
            } as StatsUserInfo));
    }, [searchData, currentUserId, selectedUsers]);

    useEffect(() => {
        loadSuggestedUsers();
    }, [currentUserId, sport]);

    // Debounce the search query
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedQuery(searchQuery);
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const loadSuggestedUsers = async () => {
        const suggested = await StatsService.getSuggestedComparisonUsers(currentUserId, sport);
        setSuggestedUsers(suggested.filter(u => !selectedUsers.some(s => s.id === u.id)));
    };

    const handleAddUser = (user: StatsUserInfo) => {
        if (selectedUsers.length < maxUsers) {
            onUsersChange([...selectedUsers, user]);
            setSearchQuery('');
            setDebouncedQuery('');
            setSuggestedUsers(suggestedUsers.filter(u => u.id !== user.id));
        }
    };

    const handleRemoveUser = (userId: string) => {
        onUsersChange(selectedUsers.filter(u => u.id !== userId));
    };

    const displayUsers = debouncedQuery.length > 0 ? searchResults : suggestedUsers;

    return (
        <div className="w-full">
            {/* Selected Users Display */}
            <div className="flex flex-wrap gap-2 mb-4">
                {selectedUsers.length === 0 ? (
                    <div className="w-full text-center py-4">
                        <p className="text-gray-400 text-sm">No comparison users selected yet</p>
                        <p className="text-gray-500 text-xs mt-1">Search below to add riders</p>
                    </div>
                ) : (
                    selectedUsers.map(user => (
                        <IonChip
                            key={user.id}
                            className="comparison-user-chip"
                        >
                            <IonAvatar className="w-6 h-6">
                                {user.profile_pic ? (
                                    <img src={user.profile_pic} alt={user.username} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-[#5861AC] to-[#324E80] flex items-center justify-center">
                                        <IonIcon icon={personCircleOutline} className="text-white text-lg" />
                                    </div>
                                )}
                            </IonAvatar>
                            <IonLabel className="font-semibold">{user.username}</IonLabel>
                            <IonIcon
                                icon={closeCircle}
                                onClick={() => handleRemoveUser(user.id)}
                                className="cursor-pointer hover:text-red-400 transition-colors"
                            />
                        </IonChip>
                    ))
                )}
            </div>

            {/* Search and User List */}
            <div>
                <EclipticSearchbar
                    value={searchQuery}
                    onIonInput={(e) => setSearchQuery(e.detail.value || '')}
                    placeholder="Search users..."
                    className="custom-searchbar mb-4 [--padding-start:12px]"
                    animated
                    showClearButton="focus"
                />

                {loading && (
                    <div className="flex justify-center py-8">
                        <IonSpinner name="crescent" style={{ color: 'var(--highlight)' }} />
                    </div>
                )}

                {!loading && (
                    <>
                        <div className="mb-2 px-2">
                            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
                                {searchQuery.length > 0 ? 'Search Results' : 'Suggested Users'}
                            </h3>
                        </div>

                        {displayUsers.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-gray-400">
                                    {searchQuery.length > 0 ? 'No users found' : 'No suggestions available'}
                                </p>
                            </div>
                        ) : (
                            <IonList className="bg-transparent">
                                {displayUsers.map(user => (
                                    <IonItem
                                        key={user.id}
                                        button
                                        onClick={() => handleAddUser(user)}
                                        className="user-item mb-2 rounded-lg"
                                        lines="none"
                                        disabled={selectedUsers.length >= maxUsers}
                                    >
                                        <IonAvatar slot="start" className="w-11 h-11">
                                            {user.profile_pic ? (
                                                <img src={user.profile_pic} alt={user.username} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-[#5861AC] to-[#324E80] flex items-center justify-center">
                                                    <IonIcon icon={personCircleOutline} className="text-white text-2xl" />
                                                </div>
                                            )}
                                        </IonAvatar>

                                        <IonLabel>
                                            <h2 className="font-semibold text-white">{user.username}</h2>
                                            <p className="text-sm text-gray-400">
                                                {user.name}
                                                {user.home_mountain && ` • ${user.home_mountain}`}
                                            </p>
                                        </IonLabel>

                                        <IonIcon
                                            icon={addCircle}
                                            slot="end"
                                            className="text-purple-500 text-2xl"
                                        />
                                    </IonItem>
                                ))}
                            </IonList>
                        )}
                    </>
                )}
            </div>


            <style>{`
        .comparison-user-chip {
          --background: rgba(31, 41, 55, 0.8);
          --color: #FFFFFF;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(107, 114, 128, 0.3);
          padding: 4px 12px;
          border-radius: 20px;
          font-weight: 600;
        }

        .custom-searchbar {
          --background: rgba(31, 41, 55, 0.8);
          --border-radius: 12px;
          --box-shadow: none;
          --placeholder-color: #9CA3AF;
          --icon-color: #9CA3AF;
          padding: 0;
        }

        .custom-searchbar .searchbar-search-icon {
          display: none;
        }

        .user-item {
          --background: rgba(31, 41, 55, 0.6);
          --background-hover: rgba(31, 41, 55, 0.8);
          --background-activated: rgba(31, 41, 55, 0.9);
          --border-radius: 12px;
          --padding-start: 12px;
          --padding-end: 12px;
          --inner-padding-end: 12px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(107, 114, 128, 0.2);
          margin-bottom: 8px;
        }


      `}</style>
        </div>
    );
};

export default UserComparisonSelector;
